import { graphql, useStaticQuery } from 'gatsby';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { ButtonContentfulModel } from 'src/api/models/Button';
import ButtonTertiary from 'src/elements/buttons/Button.tertiary';
import LinkBranded from 'src/elements/links/Link.branded';
import Heading from 'src/elements/typography/heading';
import CloseIcon from 'src/images/CloseIcon';
import {
  Confetti1,
  CloseButton,
  DescriptionTextWrapper,
  Modal,
  ModalBackdrop,
  ModalTitleWrapper,
  NoThanksButtonWrapper,
  TermsAndConditionsLabel,
  Confetti2,
  BackgroundHexagon,
  Confetti3
} from './PromotionModal.styles';

import { IGatsbyImageData } from 'gatsby-plugin-image';

const PROMOTION_MODAL_DISMISSED_FLAG = 'promotionModalDismissedFlag';
const MODAL_FADE_IN_TIMEOUT = 5000;

interface PromotionModalProps {
  currentPageId?: string;
  displayModal: boolean;
}

interface QueryResultData {
  contentfulPromotionModal: {
    title: string;
    modalFadeInTime?: number;
    description: {
      raw: string;
    };
    visibleUntil: string;
    cta: ButtonContentfulModel;
    pagesToShow: Array<{
      contentful_id: string;
    }>
  }
  file: {
    childImageSharp: { gatsbyImageData: IGatsbyImageData }
  };
}

const componentQuery = graphql`
  query PromotionModalQuery {
   contentfulPromotionModal(contentful_id: {eq: "3X6TBS8QIVZvgvJgb4iF4Q"}) {
     title
     modalFadeInTime
     description {
      raw
    }
    visibleUntil
    cta {
      text
      url
    }
  }
  file(relativePath: {eq: "background/corner-hexagon.png"}) {
    childImageSharp {
      gatsbyImageData(width: 216, layout: CONSTRAINED, placeholder: NONE)
    }
  }
}
`;

const PromotionModal = ({ displayModal }: PromotionModalProps): JSX.Element => {
  const [ isModalDismissed, setIsModalDismissed ] = useState(true);

  const data: QueryResultData = useStaticQuery(componentQuery);
  const { cta, title, description, modalFadeInTime } = data.contentfulPromotionModal;

  const linkRef = useRef(null);
  const isCurrentPageEligible = displayModal;

  const descriptionContent = documentToReactComponents(JSON.parse(description.raw));
  const fadeInTimeout = useMemo(() => modalFadeInTime ? modalFadeInTime * 1000 : MODAL_FADE_IN_TIMEOUT, [ modalFadeInTime ]);

  useEffect(() => {
    const promotionModalDismissedFlag = sessionStorage.getItem(PROMOTION_MODAL_DISMISSED_FLAG);

    if (!promotionModalDismissedFlag && displayModal) {
      setTimeout(() => {
        setIsModalDismissed(false);
      }, fadeInTimeout);
    }
  }, [ linkRef, displayModal, fadeInTimeout ]);

  // This is needed to prevent to bubble up the event to the parent element
  // and call the closeModal function since the modal needs to be anchored in it's parent
  const handleModalClick = useCallback((event) => {
    if (!linkRef.current.contains(event.target)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);

  const closeModal = useCallback(() => {
    setIsModalDismissed(true);
    sessionStorage.setItem(PROMOTION_MODAL_DISMISSED_FLAG, 'true');
  }, [ setIsModalDismissed ]);

  if (isModalDismissed || !isCurrentPageEligible) {
    return null;
  }

  const content = (
    <ModalBackdrop onClick={closeModal}>
      <Modal onClick={handleModalClick}>
        <CloseButton onClick={closeModal}>
          <CloseIcon />
        </CloseButton>

        <ModalTitleWrapper>
          <Heading text={title} size="40px" sizeSm="22px" align="center" />
        </ModalTitleWrapper>

        <DescriptionTextWrapper>
          {descriptionContent}
        </DescriptionTextWrapper>

        <div ref={linkRef}>
          <LinkBranded url={cta.url}
            text={cta.text}
            rel="noopener noreferrer" />
        </div>

        <NoThanksButtonWrapper>
          <ButtonTertiary onClick={closeModal} text="No Thanks" color="text" />
        </NoThanksButtonWrapper>

        <TermsAndConditionsLabel>Terms & Conditions apply.</TermsAndConditionsLabel>

        <Confetti1 />
        <Confetti2 />
        <Confetti3 />

        <BackgroundHexagon childImageSharp={data.file.childImageSharp.gatsbyImageData} alt="Hexagon background image" />
      </Modal>
    </ModalBackdrop>
  );

  return ReactDOM.createPortal(
    content,
    document.getElementById('portal')
  );
}

export default PromotionModal;
